import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useSession } from '@/auth';
import { navItemsFor } from '@/routes/roleRoutes';
import { messages } from '@/i18n';

/**
 * The drawer version of SideNav's list — same `navItemsFor(session.role)`
 * source, so a role never sees a different set of destinations depending on
 * viewport. `aria-label="Mobile"` keeps this <nav> distinct from SideNav's
 * `aria-label="Main"`; Radix also only mounts SheetContent while open, so the
 * two never coexist in the accessibility tree regardless.
 */
export function MobileNav() {
  const session = useSession();
  const items = navItemsFor(session.role);
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label={messages.common.openMenu}>
          <Menu />
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{messages.common.mainNavigation}</SheetTitle>
        </SheetHeader>
        <nav aria-label="Mobile" className="flex flex-col gap-1 p-4">
          {items.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={{ pathname: to, search: location.search }}
              end
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-foreground/80 hover:bg-secondary/70',
                )
              }
            >
              <Icon className="size-4 shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
